"use client";

import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import noOrder from "@/assets/images/account/no-order.webp";
import { useConfirm } from "@/components/global/confirm-context";
import { PopLoading } from "@/components/pop-ui/pop-loading";
import { toast } from "@/components/pop-ui/pop-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { PickupCodeDialog } from "@/modules/order/components/pickup-code-dialog";
import { UserOrderItemStoreGo } from "@/modules/order/components/user-order-item-store-go";
import { OrderStatusEnum } from "@/modules/order/schema";
import { useORPC } from "@/providers/orpc-provider";
import { useTracking } from "@/track";
import Empty from "./components/empty";
import { ModulePagination } from "./components/module-pagination";
import { ModuleTabs, ModuleTabsWithIndicator } from "./components/module-tabs";
import { ModuleTitle } from "./components/module-title";
const pageSize = 20;
interface OrdersStoreGoModuleProps {
  onConfirmDialogStateChange?: (isOpen: boolean) => void;
}
enum OrderTabType {
  TO_PAY = "toPay",
  PROCESSING = "processing",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}
export function OrdersStoreGoModule({
  onConfirmDialogStateChange
}: OrdersStoreGoModuleProps) {
  const {
    t
  } = useTranslation();
  const orpc = useORPC();
  const confirm = useConfirm();
  const isMobile = useIsMobile();

  // Tab相关状态
  const [activeTab, setActiveTab] = React.useState<OrderTabType>(OrderTabType.PROCESSING);

  // 分页相关状态
  const [page, setPage] = React.useState(1);

  // 取货码弹窗状态
  const [pickupCodeDialogOpen, setPickupCodeDialogOpen] = React.useState(false);
  const [currentPickupCode, setCurrentPickupCode] = React.useState("");
  const [_currentOrderNumber, setCurrentOrderNumber] = React.useState("");
  const [currentQueueCode, setCurrentQueueCode] = React.useState("");
  const {
    track,
    TrackingEventType
  } = useTracking();

  // Tab配置
  const TABS = [{
    key: OrderTabType.TO_PAY,
    label: t("account.orders.tabs.toPay")
  }, {
    key: OrderTabType.PROCESSING,
    label: t("account.orders.tabs.processing")
  }, {
    key: OrderTabType.COMPLETED,
    label: t("account.orders.tabs.completed")
  }, {
    key: OrderTabType.CANCELLED,
    label: t("account.orders.tabs.canceled")
  }];

  // 根据tab类型获取对应的订单状态数组
  const getStatusByTab = (tabType: OrderTabType): OrderStatusEnum[] => {
    switch (tabType) {
      case OrderTabType.TO_PAY:
        return [OrderStatusEnum.WAIT_PAY];
      case OrderTabType.PROCESSING:
        return [OrderStatusEnum.PAID, OrderStatusEnum.SENDED];
      case OrderTabType.COMPLETED:
        return [OrderStatusEnum.SUCCESS];
      case OrderTabType.CANCELLED:
        return [OrderStatusEnum.CLOSE, OrderStatusEnum.REJECTED, OrderStatusEnum.TIMEOUT];
      default:
        return [];
    }
  };

  // 处理分页变化
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // 重置滚动条到顶部
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // 处理tab变化，重置页码
  React.useEffect(() => {
    setPage(1);
  }, [activeTab]);

  // 统一的取消订单 mutation
  const cancelOrderMutation = useMutation({
    mutationFn: (params: {
      orderNumber: string;
      reason?: string;
    }) => orpc.ec.order.closeOrder.call(params),
    onSuccess: () => {
      toast.success(t("order.toast.cancelSuccess"));
      refetch();
    },
    onError: (error: Error) => {
      console.error("Failed to cancel order:", error);
      refetch();
    }
  });
  const handleCancelOrder = async (orderNumber: string) => {
    track("PopMartGlobalWebCancelOrderClick", {
      product_type: "mall",
      order_id: orderNumber
    }, TrackingEventType.CLICK);
    onConfirmDialogStateChange?.(true);
    try {
      const _ok = await confirm({
        title: t("order.confirm.cancelTitle"),
        description: t("order.confirm.cancelDescription"),
        closeOnError: true,
        onConfirmAction: async () => {
          await cancelOrderMutation.mutateAsync({
            orderNumber,
            reason: "用户取消"
          });
        }
      });
    } finally {
      // Notify parent that confirm dialog is closed
      onConfirmDialogStateChange?.(false);
    }
  };
  const handleShowPickupCode = (pickupCode: string, orderNumber_0: string, queueCode?: string) => {
    setCurrentPickupCode(queueCode || ""); // Use queueCode as pickupCode, empty if not available
    setCurrentOrderNumber(orderNumber_0);
    setCurrentQueueCode(pickupCode); // Use pickupCode as redemptionCode
    setPickupCodeDialogOpen(true);
  };
  const {
    data: userOrders,
    isLoading,
    refetch
  } = useQuery({
    ...orpc.storePick.storePickOrder.getUserOrders.queryOptions({
      input: {
        page: page,
        pageSize: pageSize,
        status: getStatusByTab(activeTab)
      }
    })
  });

  // 直接使用查询结果，不需要前端筛选
  const filteredOrders = userOrders?.items || [];
  const totalCount = userOrders?.total || 0;
  const TabsComponent = isMobile ? ModuleTabsWithIndicator : ModuleTabs;

  // Desktop header component
  const headerDom = <>
			{!isMobile ? <ModuleTitle title={t("account.orders.pickup.title", "門店取貨")} /> : null}
			<TabsComponent tabs={TABS} activeKey={activeTab} onChange={key => setActiveTab(key as OrderTabType)} />
		</>;

  // 加载中
  if (isLoading) {
    return <div className={isMobile ? "min-h-screen" : "min-h-[500px]"}>
				{headerDom}
				<div className={`flex justify-center items-center py-8 ${isMobile ? "px-3" : ""}`}>
					<PopLoading />
				</div>
			</div>;
  }

  // 无订单
  if (filteredOrders.length === 0) {
    return <div className={isMobile ? "min-h-screen" : ""}>
				{headerDom}
				<div className={isMobile ? "px-3 py-4" : ""}>
					<Empty src={noOrder} title={t("account.orders.pickup.emptyText", "您暫無門店取貨訂單")} />
				</div>
			</div>;
  }
  return <div>
			{headerDom}
			<div className="flex flex-col gap-2 md:gap-4 bg-muted">
				{filteredOrders.map((userOrder: any) => <UserOrderItemStoreGo key={userOrder.id} order={userOrder} onCancel={handleCancelOrder} onShowPickupCode={handleShowPickupCode} />)}
			</div>
			<ModulePagination currentPage={page} totalPages={Math.ceil(totalCount / pageSize)} onPageChange={handlePageChange} />

			{/* 取货码弹窗 */}
			<PickupCodeDialog open={pickupCodeDialogOpen} onOpenChange={setPickupCodeDialogOpen} pickupCode={currentPickupCode} redemptionCode={currentQueueCode} />
		</div>;
}